import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
   CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CFormCheck, CCardFooter, CSpinner
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DateInput from 'components/forms/DateInput';
import { jwtDecode } from 'jwt-decode';
import ToastNotification from "components/globals/ToastNotification";
import UserSelectionModal from "components/UserSelectionModal";
import TextEditor from 'components/forms/TextEditor';
import { triggerAlert } from 'components/globals/AlertNotification';

const Complaint = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const [formData, setFormData] = useState(null);
   const location = useLocation();
   const [users, setUsers] = useState([]);
   const [toast, setToast] = useState(null);
   const [isEditing, setIsEditing] = useState(true);
   const [showModal, setShowModal] = useState(false);
   const [loading, setLoading] = useState(false);

   const token = localStorage.getItem("access_token");
   const decodedToken = jwtDecode(token);
   const is_director = decodedToken.is_director;
   const user_id = decodedToken.user_id;

   const fetchComplaintDetails = async () => {
      let data = null; // Declare a variable to store API response

      try {
         const response = await apiClient.get(`/complaints/${id}/`);
         data = response.data; // Store response data
         setFormData(data); // Update state
      } catch (error) {
         triggerAlert('Reclamação não foi encontrada!', 'error');
         navigate("/reclamacoes");
      } finally {
         setIsEditing(data?.approved === null); // Use the local variable, NOT formData
      }
   };

   useEffect(() => {
      const fetchUsers = async () => {
         try {
            const response = await apiClient.get('/users/active/');
            setUsers(response.data);
         } catch (error) {
            console.error('Error fetching users:', error);
         }
      };

      if (!id) {
         setFormData({ received_by: user_id, responsible_members: [] });
         setIsEditing(true);
      } else {
         fetchComplaintDetails();
      }
      fetchUsers();
   }, [id, location.pathname]);

   const handleCheckboxUsersChange = (userId, isChecked) => {
      setFormData((prevData) => {
         let updatedMembers = isChecked
            ? [...prevData.responsible_members, userId]
            : prevData.responsible_members.filter((id) => id !== userId);

         return { ...prevData, responsible_members: updatedMembers };
      });
   };

   const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;
      let updatedValue;

      if (type === "checkbox") {
         updatedValue = checked; // true if checked, false if unchecked
      } else if (value === "true") {
         updatedValue = true;
      } else if (value === "false") {
         updatedValue = false;
      } else {
         updatedValue = value || null; // Convert empty values to null
      }

      setFormData((prevFormData) => {
         let updatedForm = {
            ...prevFormData,
            [name]: updatedValue,
         };

         if (name === "action" && !updatedValue) {
            updatedForm.action_text = "";
         }

         if (name === "approved" && updatedValue !== "") {
            updatedForm.handled_date = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
         }

         return updatedForm;
      });

      setIsEditing(true);
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      try {
         if (id) {
            await apiClient.put(`/complaints/${id}/`, formData);
            triggerAlert('Reclamação atualizada com sucesso!', 'success');
            navigate(`/reclamacoes`);
         } else {
            await apiClient.post('/complaints/', formData);
            triggerAlert('Reclamação criada com sucesso!', 'success');
            navigate('/reclamacoes');
         }
      } catch (error) {
         const errorMessage = error.response?.data?.error || 'Erro ao gravar';
         setToast({ title: 'Reclamaçãa', message: { errorMessage }, type: 'error' });
         setLoading(false);
      }
   };

   if (formData !== null) {
      if (!isEditing && (formData.approved !== null || (user_id !== formData.received_by && !is_director))) {
         return (

            <div className="container mt-4">
               <h4 className="text-center mb-5">Detalhes da Reclamação</h4>
               {[
                  { label: "Código", value: formData.code },
                  { label: "Cliente/Fornecedor", value: formData.entity || "-" },
                  { label: "Pessoa a contactar", value: formData.contact_person || "-" },
                  { label: "Contacto telefónico", value: formData.contact_phone || "-" },
                  { label: "Email", value: formData.contact_email || "-" },
                  { label: "Descrição", value: <div dangerouslySetInnerHTML={{ __html: formData.description || "-" }} /> },
                  { label: "Recebido por", value: formData.received_by_name || "-" },
                  { label: "Análise da reclamação", value: <div dangerouslySetInnerHTML={{ __html: formData.analysis || "-" }} /> },
                  { label: "É necessário plano de contingência?", value: formData.contingency_plan ? "Sim" : "Não" },

                  // Only show 'Se sim, qual?' if contingency_plan is true
                  formData.contingency_plan ? { label: "Se sim, qual?", value: <div dangerouslySetInnerHTML={{ __html: formData.contingency_description || "-" }} /> } : null,

                  { label: "O problema é generalizável?", value: formData.generalizable ? "Sim" : "Não" },
                  { label: "Ação corretiva?", value: formData.action ? "Sim" : "Não" },

                  // Only show 'Se sim, qual?' if action is true
                  formData.action ? { label: "Se sim, qual?", value: <div dangerouslySetInnerHTML={{ __html: formData.action_description || "-" }} /> } : null,

                  {
                     label: "Responsáveis",
                     value: formData.responsible_members.length > 0 ? (
                        <ul>
                           {users
                              .filter((user) => formData.responsible_members.includes(user.id))
                              .map((user) => (
                                 <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                              ))}
                        </ul>
                     ) : (
                        "Nenhum responsável selecionado"
                     ),
                  },
                  { label: "Aprovado", value: formData.approved === true ? "Sim" : formData.approved === false ? "Não" : "-" },
                  { label: "Data", value: formData.handled_date || "-" },
               ]
                  .filter(Boolean) // Removes `null` values from the array (handles conditional rendering)
                  .map((item, index) => (
                     <CCard key={index} className="mb-3">
                        <CCardHeader>
                           <h5>{item.label}</h5>
                        </CCardHeader>
                        <CCardBody>
                           <div>{item.value}</div>
                        </CCardBody>
                     </CCard>
                  ))}


               {/* Back button */}
               <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
                  <CButton color="danger" className="text-white" onClick={() => navigate('/complaints')}>
                     Voltar
                  </CButton>
               </div>
            </div>
         );
      }

      return (
         <div className="container mt-4">
            {/* Error Toast - Appears on submit failure */}
            <ToastNotification toast={toast} setToast={setToast} />

            <CCard>
               <CCardHeader>
                  <h4>{id ? 'Editar Reclamação' : 'Nova Reclamação'}</h4>
               </CCardHeader>
               <CCardBody>
                  <CForm onSubmit={handleSubmit}>
                     {/* Show "Código" field only when editing */}
                     {id && (
                        <CFormInput
                           type="text"
                           label="Código"
                           name="code"
                           value={formData.code}
                           readOnly
                           className="mb-3"
                        />
                     )}

                     <CFormSelect
                        label={<strong>Cliente/Fornecedor</strong>}
                        name="entity"
                        value={formData.entity ?? ''}
                        onChange={handleInputChange}
                        className="mb-3"
                        required={true}
                     >
                        <option value="">Selecione uma opção</option>
                        <option value="Cliente">Cliente</option>
                        <option value="Fornecedor">Fornecedor</option>
                     </CFormSelect>

                     <CFormInput
                        type="text"
                        label={<strong>Pessoa a contactar</strong>}
                        name="contact_person"
                        value={formData.contact_person ?? ''}
                        onChange={handleInputChange}
                        className="mb-3"
                     />

                     <CFormInput
                        type="text"
                        label={<strong>Contacto telefónico</strong>}
                        name="contact_phone"
                        value={formData.contact_phone ?? ''}
                        onChange={handleInputChange}
                        className="mb-3"
                     />

                     <CFormInput
                        type="text"
                        label={<strong>Email</strong>}
                        name="contact_email"
                        value={formData.contact_email ?? ''}
                        onChange={handleInputChange}
                        className="mb-3"
                     />

                     <TextEditor
                        label="Descrição"
                        value={formData.description ?? ''}
                        onChange={(value) =>
                           handleInputChange({ target: { name: "description", value } })
                        }
                        className="mb-3"
                     />

                     <CFormSelect
                        label={<strong>Recebido por</strong>}
                        name="received_by"
                        value={formData.received_by || user_id} // Default to logged-in user
                        onChange={handleInputChange}
                        className="mb-3"
                        required={true}
                     >
                        <option value="">Selecione um colaborador</option>
                        {users.map((user) => (
                           <option key={user.id} value={user.id}>
                              {user.first_name} {user.last_name}
                           </option>
                        ))}
                     </CFormSelect>

                     {is_director && (
                        <>
                           <TextEditor
                              label="Análise da reclamação"
                              value={formData.analysis ?? ''}
                              onChange={(value) =>
                                 handleInputChange({ target: { name: "analysis", value } })
                              }
                              className="mb-3"
                           />

                           <div className="mt-3 mb-3">
                              <div><strong>É necessário plano de contigência?</strong></div>
                              <CFormCheck
                                 name="contingency_plan"
                                 checked={formData.contingency_plan}
                                 onChange={handleInputChange}
                              />
                           </div>

                           {formData.contingency_plan && (
                              <TextEditor
                                 label="Se sim, qual?"
                                 value={formData.contingency_description ?? ''}
                                 onChange={(value) =>
                                    handleInputChange({ target: { name: "contingency_description", value } })
                                 }
                                 className="mb-3"
                              />
                           )}

                           <div className="mt-3 mb-3">
                              <div><strong>O problema é generaliz&aacute;vel?</strong></div>
                              <CFormCheck
                                 name="generalizable"
                                 checked={formData.generalizable}
                                 onChange={handleInputChange}
                              />
                           </div>

                           <div className="mt-3 mb-3">
                              <div><strong>Ação corretiva?</strong></div>
                              <CFormCheck
                                 name="action"
                                 checked={formData.action}
                                 onChange={handleInputChange}
                              />
                           </div>

                           {formData.action && (
                              <TextEditor
                                 label="Se sim, qual?"
                                 value={formData.action_description ?? ''}
                                 onChange={(value) =>
                                    handleInputChange({ target: { name: "action_description", value } })
                                 }
                                 className="mb-3"
                              />
                           )}

                           <CButton color="info" className="text-white" onClick={() => setShowModal(true)}>
                              Selecionar Responsáveis
                           </CButton>

                           {/* Display selected users */}
                           <div className="mt-2">
                              {formData.responsible_members.length > 0 ? (
                                 <ul>
                                    {users
                                       .filter((user) => formData.responsible_members.includes(user.id))
                                       .map((user) => (
                                          <li key={user.id}>{`${user.first_name} ${user.last_name}`}</li>
                                       ))}
                                 </ul>
                              ) : (
                                 <p className="text-muted">Nenhum responsável selecionado</p>
                              )}
                           </div>


                           <UserSelectionModal
                              title="Selecionar Responsáveis"
                              visible={showModal}
                              onClose={() => setShowModal(false)}
                              users={users}
                              selectedUsers={formData.responsible_members}
                              onChange={handleCheckboxUsersChange}
                           />

                           <CFormSelect
                              label={<strong>Aprovado</strong>}
                              name="approved"
                              value={formData.approved ?? ''}
                              onChange={handleInputChange}
                              className="mb-3"
                           >
                              <option value="">Selecione</option>
                              <option value="true">Sim</option>
                              <option value="false">Não</option>
                           </CFormSelect>

                           <DateInput
                              label={<strong>Data</strong>}
                              name="handled_date"
                              value={formData.handled_date ?? ''}
                              onChange={handleInputChange}
                           />
                        </>
                     )}
                  </CForm>
               </CCardBody>
               <CCardFooter className="d-flex justify-content-end gap-2">
                  <CButton color="danger" className="text-white" onClick={() => navigate("/complaints")}>
                     Cancelar
                  </CButton>
                  <CButton disabled={loading} color="success" className="text-white" type="submit" onClick={handleSubmit}>
                     {loading ? <CSpinner size="sm" /> : 'Confirmar'}
                  </CButton>
               </CCardFooter>
            </CCard>
         </div>
      );
   }
};

export default Complaint;
